import { useCallback, useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { ReadOnlyField } from '@/components/atoms/ReadOnlyField/ReadOnlyField';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionFieldsetProps, FacebookIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CHANNELS_URL } from '@/util/constants';
import { errorMessage, trimOnBlur } from '@/util/validation/helpers';
import { integrationRulesFacebook } from '@/util/validation/rules/integration-rules';

import styles from './ManualSettings.module.scss';

type Form = { page_id: string; access_token: string; verify_token: string; app_secret: string };

const ManualSettings = ({
  type,
  integration,
  registerAccordion,
  toggleAccordion,
}: AccordionFieldsetProps<Form, FacebookIntegration>) => {
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const dispatch = useDispatch();

  const callbackUrl = `${CHANNELS_URL}/v1/${integration?.type}/${integration?.integration_id}`;

  const { updateIntegration, updateStatus } = useIntegrations<FacebookIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );
  const formMethods = useForm<Form>({ mode: 'onSubmit' });

  const {
    control,
    reset,
    formState: { errors, isDirty },
    handleSubmit,
    getValues,
    trigger,
  } = formMethods;
  const pageIdErrorMessage = errorMessage({
    field: errors.page_id,
    maxLength: integrationRulesFacebook.pageId.maxLength,
  });

  const accessTokenErrorMessage = errorMessage({
    field: errors.access_token,
    maxLength: integrationRulesFacebook.accessToken.maxLength,
  });

  const verifyTokenErrorMessage = errorMessage({
    field: errors.verify_token,
    maxLength: integrationRulesFacebook.verifyToken.maxLength,
  });

  const appSecretErrorMessage = errorMessage({
    field: errors.app_secret,
    maxLength: integrationRulesFacebook.appSecret.maxLength,
  });

  useEffect(() => {
    reset({
      page_id: integration?.config?.page_id ?? '',
      access_token: integration?.config?.access_token ?? '',
      verify_token: integration?.config?.verify_token ?? '',
      app_secret: integration?.config?.app_secret ?? '',
    });
  }, [
    integration?.config?.access_token,
    integration?.config?.app_secret,
    integration?.config?.page_id,
    integration?.config?.verify_token,
    reset,
  ]);

  const handleUpdateIntegration = useCallback(() => {
    const { page_id, access_token, verify_token, app_secret } = getValues();
    updateIntegration(
      {
        ...integration,
        config: {
          ...integration?.config,
          page_id: page_id || '',
          access_token: access_token || '',
          verify_token: verify_token || '',
          app_secret: app_secret || '',
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, getValues, integration, type, updateIntegration]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  // for reseting changes
  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [reset, expanded, isDirty, type]);

  return (
    <Accordion
      title={t('integrations.manual_settings.title')}
      subtitle={t('integrations.manual_settings.subtitle')}
      onSubmit={handleSubmit(handleUpdateIntegration)}
      disabled={!isDirty}
      isLoading={updateStatus === 'pending'}
      expanded={expanded === type}
      handleChange={toggleAccordion(type)}
      ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
      readOnly={!canWrite}
    >
      <>
        <TitleSubtitle
          title={t('integrations.manual_settings.callback_url.title')}
          subtitle={t('integrations.manual_settings.callback_url.subtitle')}
        />

        <div className={styles.callback_url}>
          <ReadOnlyField className="flex-1" value={callbackUrl} />
          <CopyButton className={styles.copy_url} data={callbackUrl} />
        </div>

        <TitleSubtitle
          title={t('integrations.manual_settings.page_id.title')}
          subtitle={t('integrations.manual_settings.page_id.subtitle')}
          error={!!errors.page_id}
        />

        <div className={styles.text_area}>
          <Controller
            control={control}
            name="page_id"
            rules={{
              required: t('validation.required'),
              maxLength: { value: integrationRulesFacebook.pageId.maxLength, message: '' },
            }}
            render={({ field }) => (
              <Input
                id="page_id"
                placeholder=""
                aria-invalid={!!errors.page_id}
                readOnly={!canWrite}
                {...field}
                value={field.value ?? ''}
                onBlur={trimOnBlur(field)}
              />
            )}
          />
          {pageIdErrorMessage && <p className="mt-1 text-sm text-warning">{pageIdErrorMessage}</p>}
        </div>

        <TitleSubtitle
          title={t('integrations.manual_settings.access_token.title')}
          subtitle={t('integrations.manual_settings.access_token.subtitle')}
          error={!!errors.access_token}
        />

        <div className={styles.text_area}>
          <Controller
            control={control}
            name="access_token"
            rules={{
              required: t('validation.required'),
              maxLength: { value: integrationRulesFacebook.accessToken.maxLength, message: '' },
            }}
            render={({ field }) => (
              <Input
                id="access_token"
                placeholder=""
                aria-invalid={!!errors.access_token}
                readOnly={!canWrite}
                {...field}
                value={field.value ?? ''}
                onBlur={trimOnBlur(field)}
              />
            )}
          />
          {accessTokenErrorMessage && (
            <p className="mt-1 text-sm text-warning">{accessTokenErrorMessage}</p>
          )}
        </div>

        <TitleSubtitle
          title={t('integrations.manual_settings.verify_token.title')}
          subtitle={t('integrations.manual_settings.verify_token.subtitle')}
          error={!!errors.verify_token}
        />

        <div className={styles.text_area}>
          <Controller
            control={control}
            name="verify_token"
            rules={{
              required: t('validation.required'),
              maxLength: { value: integrationRulesFacebook.verifyToken.maxLength, message: '' },
            }}
            render={({ field }) => (
              <Input
                id="verify_token"
                placeholder=""
                aria-invalid={!!errors.verify_token}
                readOnly={!canWrite}
                {...field}
                value={field.value ?? ''}
                onBlur={trimOnBlur(field)}
              />
            )}
          />
          {verifyTokenErrorMessage && (
            <p className="mt-1 text-sm text-warning">{verifyTokenErrorMessage}</p>
          )}
        </div>

        <TitleSubtitle
          title={t('integrations.manual_settings.app_secret.title')}
          subtitle={t('integrations.manual_settings.app_secret.subtitle')}
          error={!!errors.app_secret}
        />

        <div className={styles.text_area}>
          <Controller
            control={control}
            name="app_secret"
            rules={{
              required: t('validation.required'),
              maxLength: { value: integrationRulesFacebook.appSecret.maxLength, message: '' },
            }}
            render={({ field }) => (
              <Input
                id="app_secret"
                placeholder=""
                aria-invalid={!!errors.app_secret}
                readOnly={!canWrite}
                {...field}
                value={field.value ?? ''}
                onBlur={trimOnBlur(field)}
              />
            )}
          />
          {appSecretErrorMessage && (
            <p className="mt-1 text-sm text-warning">{appSecretErrorMessage}</p>
          )}
        </div>
      </>
    </Accordion>
  );
};

export default ManualSettings;
